import {
  CashflowResponse,
  CloseExceptionResponse,
  ExceptionListResponse,
  type ExceptionKind,
  type ExceptionStatus,
  InvoiceDetailResponse,
  InvoiceListResponse,
  OverviewResponse,
  RunReconResponse,
} from '@serievo/contracts';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Work } from '../features/context/WorkContext';
import { apiGet, apiPost } from './client';

const key = (w: Work) => [w.tenant.id, w.query.toString()];

export function useOverview(w: Work) {
  return useQuery({
    queryKey: ['overview', ...key(w)],
    queryFn: ({ signal }) => apiGet(`/overview?${w.query}`, OverviewResponse, { tenantId: w.tenant.id, signal }),
    select: (r) => r.data,
    placeholderData: keepPreviousData,
  });
}

export function useInvoices(w: Work, f: { kind?: string | undefined; q?: string | undefined }) {
  return useInfiniteQuery({
    queryKey: ['invoices', ...key(w), f.kind ?? '', f.q ?? ''],
    initialPageParam: '',
    queryFn: ({ pageParam, signal }) => {
      const p = new URLSearchParams(w.query);
      p.set('limit', '50');
      if (f.kind) p.set('kind', f.kind);
      if (f.q) p.set('q', f.q);
      if (pageParam) p.set('cursor', pageParam);
      return apiGet(`/invoices?${p}`, InvoiceListResponse, { tenantId: w.tenant.id, signal });
    },
    getNextPageParam: (last) => last.meta?.next_cursor ?? undefined,
    placeholderData: keepPreviousData,
  });
}

export function useInvoiceDetail(w: Work, id: string | null) {
  return useQuery({
    queryKey: ['invoice', w.tenant.id, w.entity.id, id],
    queryFn: ({ signal }) =>
      apiGet(`/invoices/${id}?entity=${w.entity.id}`, InvoiceDetailResponse, { tenantId: w.tenant.id, signal }),
    select: (r) => r.data,
    enabled: !!id,
  });
}

export function useExceptions(w: Work, f: { status?: ExceptionStatus | undefined; kind?: ExceptionKind | undefined }) {
  const monthly = /^\d{4}-\d{2}$/.test(w.period);
  return useQuery({
    queryKey: ['exceptions', w.tenant.id, w.entity.id, w.period, f.status ?? '', f.kind ?? ''],
    queryFn: ({ signal }) => {
      const p = new URLSearchParams({ entity: w.entity.id, period: w.period });
      if (f.status) p.set('status', f.status);
      if (f.kind) p.set('kind', f.kind);
      return apiGet(`/recon/r1/exceptions?${p}`, ExceptionListResponse, { tenantId: w.tenant.id, signal });
    },
    select: (r) => r.data,
    enabled: monthly,
    placeholderData: keepPreviousData,
  });
}

export function useCloseException(w: Work) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; conclusion: string }) =>
      apiPost(`/recon/r1/exceptions/${v.id}/close`, { entity: w.entity.id, conclusion: v.conclusion }, CloseExceptionResponse, {
        tenantId: w.tenant.id,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['exceptions'] });
      void qc.invalidateQueries({ queryKey: ['overview'] });
    },
  });
}

export function useRunR1(w: Work) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiPost('/recon/r1/run', { entity: w.entity.id, period: w.period }, RunReconResponse, { tenantId: w.tenant.id }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['exceptions'] });
      void qc.invalidateQueries({ queryKey: ['overview'] });
    },
  });
}

export function useCashflow(w: Work) {
  return useQuery({
    queryKey: ['cashflow', ...key(w)],
    queryFn: ({ signal }) => apiGet(`/cashflow?${w.query}`, CashflowResponse, { tenantId: w.tenant.id, signal }),
    select: (r) => r.data,
    placeholderData: keepPreviousData,
  });
}
